/**
 * GPX Module Scripts
 * Lightweight lazy loading for extra images + optional video.
 * - Extras (images/video) wait until near/in view.
 * - Video: muted, looped, plays/pause on visibility; keeps last frame.
 * - Fallback: without IntersectionObserver, everything loads immediately.
 */
document.addEventListener('DOMContentLoaded', () => {
	const SLIDE_INTERVAL = 5000;
	const cards = Array.from(document.querySelectorAll('.gpx'));
	const supportIO = 'IntersectionObserver' in window;
	const opts = { root: null, rootMargin: '200px 0px', threshold: 0.15 };

	const state = new WeakMap();

	const setupCard = (card) => {
		if (state.has(card)) return;
		const media = card.querySelector('.gpx-media');
		const base = media.querySelector('.gpx-img');
		const additional = (card.dataset.additional || '')
			.split(',')
			.map(s => s.trim())
			.filter(Boolean);
		const videoUrl = card.dataset.video;

		state.set(card, {
			media,
			base,
			additional,
			slides: [base],
			extrasLoaded: false,
			interval: null,
			current: 0,
			inView: false,
			videoUrl,
			videoEl: null,
			videoReady: false
		});
	};

	const startRotation = (card) => {
		const s = state.get(card);
		if (!s || s.interval || !s.extrasLoaded || s.slides.length < 2 || !s.inView) return;
		s.interval = setInterval(() => advance(card), SLIDE_INTERVAL);
	};

	const stopRotation = (card) => {
		const s = state.get(card);
		if (s?.interval) {
			clearInterval(s.interval);
			s.interval = null;
		}
	};

	const advance = (card) => {
		const s = state.get(card);
		if (!s || s.slides.length < 2) return;
		s.slides[s.current].classList.remove('active');
		s.current = (s.current + 1) % s.slides.length;
		s.slides[s.current].classList.add('active');
	};

	const loadExtras = (card) => {
		const s = state.get(card);
		if (!s || s.extrasLoaded || !s.additional.length) return;
		const { media } = s;
		s.additional.forEach(url => {
			const img = document.createElement('img');
			img.src = url;
			img.loading = 'lazy';
			img.decoding = 'async';
			img.className = 'gpx-img';
			media.appendChild(img);
			s.slides.push(img);
		});
		s.extrasLoaded = true;
		startRotation(card);
	};

	const ensureVideo = (card) => {
		const s = state.get(card);
		if (!s?.videoUrl || s.videoEl) {
			if (s?.videoEl && s.inView && s.videoReady) s.videoEl.play().catch(() => {});
			return;
		}

		const video = document.createElement('video');
		video.className = 'gpx-img gpx-video';
		video.muted = true;
		video.loop = true;
		video.playsInline = true;
		video.autoplay = true;
		video.preload = 'none';
		video.src = s.videoUrl;
		video.setAttribute('muted', '');
		video.setAttribute('playsinline', '');
		video.setAttribute('autoplay', '');
		video.setAttribute('loop', '');

		video.addEventListener('canplay', () => {
			s.videoReady = true;
			if (s.inView) {
				video.classList.add('active');
				video.play().catch(() => {});
			}
		}, { once: true });

		video.addEventListener('error', () => {
			console.warn('GPX Video failed to load:', s.videoUrl);
			s.videoReady = false;
		});

		s.media.appendChild(video);
		s.videoEl = video;

		if (s.inView) {
			video.load();
		}
	};

	const pauseVideo = (card) => {
		const v = state.get(card)?.videoEl;
		if (v) v.pause();
	};

	const onEnter = (card) => {
		const s = state.get(card);
		if (!s) return;
		s.inView = true;
		loadExtras(card);
		startRotation(card);
		ensureVideo(card);
	};

	const onExit = (card) => {
		const s = state.get(card);
		if (!s) return;
		s.inView = false;
		stopRotation(card);
		pauseVideo(card);
	};

	cards.forEach(setupCard);

	if (supportIO) {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					onEnter(entry.target);
				} else {
					onExit(entry.target);
				}
			});
		}, opts);
		cards.forEach(card => observer.observe(card));
	} else {
		// Fallback for ancient browsers
		cards.forEach(card => {
			const s = state.get(card);
			if (!s) return;
			s.inView = true;
			loadExtras(card);
			startRotation(card);
			ensureVideo(card);
		});
	}
});
